package com.patronat.top.features.usermanagement.presentation

import androidx.lifecycle.viewModelScope
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.models.profiles.UserFilter
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.UserStatus
import com.patronat.top.features.usermanagement.domain.usecase.GetUsersUseCase
import com.patronat.top.features.usermanagement.domain.usecase.UpdateUserStatusCommand
import com.patronat.top.features.usermanagement.domain.usecase.UpdateUserStatusUseCase
import com.patronat.top.features.usermanagement.domain.usecase.command.GetFilteredUsersCommand
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import top.design_system.generated.resources.Res
import top.design_system.generated.resources.error_alert_title

class UsersViewModel(
    private val alertController: AlertController,
    private val getUsersUseCase: GetUsersUseCase,
    private val updateUserStatusUseCase: UpdateUserStatusUseCase
) : BaseViewModel<UsersState, UsersEvent, UsersEffect>(
    initialState = UsersState()
) {

    private var searchJob: Job? = null

    override fun onEvent(event: UsersEvent) {
        when (event) {
            UsersEvent.OnAddUserClicked -> sendEffect(UsersEffect.NavigateToCreateUser)
            is UsersEvent.OnLoadUsers -> {
                updateState { it.copy(targetRoles = event.roles, selectedRoles = emptyList(), selectedStatuses = emptyList()) }
                loadData(page = 1)
            }
            is UsersEvent.OnPageChanged -> loadData(page = event.page)
            is UsersEvent.OnPageSizeChanged -> {
                updateState { it.copy(pageSize = event.pageSize, currentPage = 1) }
                loadData(page = 1)
            }
            is UsersEvent.OnSearch -> {
                updateState { it.copy(searchQuery = event.query) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(500)
                    loadData(page = 1)
                }
            }
            is UsersEvent.OnFilterChanged -> {
                updateState { it.copy(selectedStatuses = event.statuses) }
                loadData(page = 1)
            }
            is UsersEvent.OnRoleFilterChanged -> {
                updateState { it.copy(selectedRoles = event.roles) }
                loadData(page = 1)
            }
            is UsersEvent.OnUserAction -> updateState { it.copy(expandedMenuUser = event.user) }
            UsersEvent.OnDismissMenu -> updateState { it.copy(expandedMenuUser = null) }
            is UsersEvent.OnEditProfile -> {
                updateState { it.copy(expandedMenuUser = null) }
                event.user.id?.let { sendEffect(UsersEffect.NavigateToEditUser(it)) }
            }
            is UsersEvent.OnBlockUser -> {
                event.user.id?.let { updateUserStatus(it, UserStatus.BLOCKED) }
            }
            is UsersEvent.OnUnblockUser -> {
                event.user.id?.let { updateUserStatus(it, UserStatus.ACTIVE) }
            }
        }
    }

    private fun updateUserStatus(userId: String, newStatus: UserStatus) {
        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isLoading = isLoading, expandedMenuUser = null) } },
            request = { updateUserStatusUseCase.execute(UpdateUserStatusCommand(userId, newStatus)) },
            onSuccess = {
                loadData(currentState.currentPage)
            },
            onError = { _, message ->
                val errorText = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.error_alert_title)
                handleError(errorText)
            }
        )
    }

    private fun loadData(page: Int) {
        val rolesToFilter = if (currentState.selectedRoles.isNotEmpty()) {
            currentState.selectedRoles
        } else {
            currentState.targetRoles
        }

        val filter = UserFilter(
            roles = rolesToFilter,
            page = page,
            pageSize = currentState.pageSize,
            query = currentState.searchQuery.ifBlank { null },
            statuses = currentState.selectedStatuses.ifEmpty { null }
        )

        launchNetwork(
            onLoading = { isLoading -> updateState { it.copy(isLoading = isLoading) } },
            request = { getUsersUseCase.execute(GetFilteredUsersCommand(filter)) },
            onSuccess = { filteredUsers ->
                updateState {
                    it.copy(
                        users = filteredUsers.items,
                        totalCount = filteredUsers.totalCount,
                        currentPage = page
                    )
                }
            },
            onError = { _, message ->
                updateState { it.copy(error = message) }
                val errorText = message?.let { UiText.DynamicString(it) }
                    ?: UiText.StringResource(Res.string.error_alert_title)
                handleError(errorText)
            }
        )
    }

    private fun handleError(message: UiText) {
        alertController.show(
            title = UiText.StringResource(Res.string.error_alert_title),
            message = message,
            type = NotificationType.ERROR
        )
    }
}